import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AllCharacterHttpParamsEncoder } from '@r1-compass-library/utilities';
import { SpinnerService } from '../spinner.service';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { Observable, catchError, finalize, of, retry } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { AssetSearch } from '@r1-compass-library/models/search/asset-search.model';
import { AssetMetadata, BasicFolder } from '@r1-compass-library/models';
import { AdvancedSearch } from '@r1-compass-library/models/search/advanced-search.model';

export interface CopyIssue {
    code: string;
    message: string;
    details?: string[];
}

export interface AssetCopyResult<T> {
    copiedAsset: T;
    issues?: CopyIssue[];
}

@Injectable({ providedIn: 'root' })
export abstract class AssetRestService<T extends AssetMetadata, S extends AssetSearch> {
    protected readonly http: HttpClient = inject(HttpClient);
    protected readonly spinnerService: SpinnerService = inject(SpinnerService);

    protected abstract BASE_URL: string;
    protected abstract TARGET_OBJECT: string;
    abstract processNamePrefix: string;

    search(search: S, additionalHeaders: any = {}): Observable<T[]> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams({
                encoder: new AllCharacterHttpParamsEncoder(),
            }),
        };

        this.setSearchParams(search, httpOptions);

        const process = `${this.processNamePrefix}.search`;
        this.spinnerService.addProcess(process, `Searching ${this.TARGET_OBJECT}s...`);
        return this.http.get<T[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(err => of<T[]>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    /**
     * If you want to use this method make sure it is the correct one. This method bypasses all backend security checks
     * and just retrieves the asset for the given id. If this is retrieving data for a user it cannot use this (and
     * should likely only be used in the admin settings where admins have more ability to interact across divisions).
     * @param id
     * @param additionalHeaders
     */
    get(id: number, additionalHeaders: any = {}): Observable<T> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.get`;
        this.spinnerService.addProcess(process, `Getting ${this.TARGET_OBJECT}...`);
        return this.http.get<T>(`${this.BASE_URL}/${id}`, httpOptions).pipe(
            retry(0),
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    create(item: Partial<T>, additionalHeaders: any = {}): Observable<T> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.create`;
        this.spinnerService.addProcess(process, `Creating ${this.TARGET_OBJECT}...`);
        return this.http.post<T>(`${this.BASE_URL}`, item, httpOptions).pipe(
            retry(0),
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    update(id: number, item: Partial<T>, additionalHeaders: any = {}): Observable<T> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        const process = `${this.processNamePrefix}.update`;
        this.spinnerService.addProcess(process, `Updating ${this.TARGET_OBJECT}...`);
        return this.http.patch<T>(`${this.BASE_URL}/${id}`, item, httpOptions).pipe(
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    copy(assetId: number, userId: number | undefined, additionalHeaders: any = {}): Observable<AssetCopyResult<T>> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.copy`;
        this.spinnerService.addProcess(process, `Copying ${this.TARGET_OBJECT}...`);
        return this.http
            .post<AssetCopyResult<T>>(`${this.BASE_URL}/${assetId}/copy`, { userId: userId }, httpOptions)
            .pipe(
                catchError(() => of<AssetCopyResult<T>>()),
                finalize(() => this.spinnerService.completeProcess(process))
            );
    }

    move(item: Partial<T>, destinationFolderId: number | null, additionalHeaders: any = {}): Observable<T> {
        const assetId = item.id;
        item = { ...item, folderId: destinationFolderId };
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.move`;
        this.spinnerService.addProcess(process, `Moving ${this.TARGET_OBJECT}...`);
        return this.http.put<T>(`${this.BASE_URL}/${assetId}/move`, item, httpOptions).pipe(
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    delete(assetId: number, additionalHeaders: any = {}): Observable<T> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.delete`;
        this.spinnerService.addProcess(process, `Deleting ${this.TARGET_OBJECT}...`);
        return this.http.delete<T>(`${this.BASE_URL}/${assetId}`, httpOptions).pipe(
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    protected setSearchParams(search: S, httpOptions: { headers: HttpHeaders; params: HttpParams }) {
        for (const [key, value] of Object.entries(search)) {
            if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
                httpOptions.params = httpOptions.params.set(key, value);
            } else if (Array.isArray(value)) {
                this.addArrayParams(key, value, httpOptions);
            } else {
                console.log('Search type is not yet supported. Type: ' + typeof value);
            }
        }
    }

    private addArrayParams(key: string, value: any[], httpOptions: { headers: HttpHeaders; params: HttpParams }) {
        if (key === 'advancedSearch' || key === 'configSearch') {
            this.addAdvancedSearchParams(key, value, httpOptions);
        } else {
            value.forEach(val => {
                if (val !== undefined && val !== null) {
                    httpOptions.params = httpOptions.params.append(key, val);
                }
            });
        }
    }

    private addAdvancedSearchParams(
        key: string,
        advancedSearch: AdvancedSearch<BasicFolder>[],
        httpOptions: { headers: HttpHeaders; params: HttpParams }
    ): void {
        advancedSearch.forEach((searchCriteria, i) => {
            httpOptions.params = httpOptions.params.set(`${key}[${i}].property`, searchCriteria.property);
            httpOptions.params = httpOptions.params.set(`${key}[${i}].value`, searchCriteria.value);
            if (searchCriteria.operator) {
                httpOptions.params = httpOptions.params.set(`${key}[${i}].operator`, searchCriteria.operator);
            }
        });
    }
}
